import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { getSessionBoundary } from "@/lib/api/session";
import { loginSchema, passwordSchema } from "../model/schema";
import { useAccount } from "../model/context";

interface Values { username: string; password: string; current_password: string; new_password: string; confirm_password: string }
export function AccountForm({ password = false }: { password?: boolean }) {
  const account = useAccount()!;
  const { t, i18n } = useTranslation();
  const [failed, setFailed] = useState(false);
  const { register, handleSubmit, reset, formState } = useForm<Values>({ resolver: zodResolver(password ? passwordSchema : loginSchema) });
  const { state, controller } = account;
  const formGeneration = getSessionBoundary().generation;
  const submit = handleSubmit(async (values) => {
    const current = controller.getSnapshot();
    if (getSessionBoundary().generation !== formGeneration || current.status !== state.status || current.session !== state.session) return;
    setFailed(false);
    try {
      if (password) await controller.changePassword(values.current_password, values.new_password);
      else await controller.login(values.username, values.password);
      reset();
    } catch { reset({ ...values, password: "", current_password: "", new_password: "", confirm_password: "" }); setFailed(true); }
  });
  const fields: Array<keyof Values> = password ? ["current_password", "new_password", "confirm_password"] : ["username", "password"];
  return <section className="mx-auto w-full max-w-md rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-9">
    <div className="mb-8 flex items-center justify-between gap-3">
      <span className="font-display text-xl font-semibold">OranSim</span>
      <Button type="button" variant="ghost" size="sm" aria-label={t("topbar.switchLanguage")}
        onClick={() => void i18n.changeLanguage(i18n.language.startsWith("zh") ? "en" : "zh")}>{t("topbar.currentLanguage")}</Button>
    </div>
    <h1 className="text-xl font-semibold">{t(password ? "account.changePassword" : "account.signIn")}</h1>
    <p className="mb-6 mt-2 text-sm text-muted-foreground">{t(password
      ? state.session?.user.must_change_password ? "account.firstPasswordHint" : "account.passwordHint"
      : "account.signInHint")}</p>
    {state.notice && <p role="status" className="mb-4 text-sm text-muted-foreground">{t(`account.${state.notice}`)}</p>}
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      {fields.map((field) => <div key={field}>
        <label htmlFor={`account-${field}`} className="mb-1 block text-sm">{t(`account.${field}`)}</label>
        <input id={`account-${field}`} type={field === "username" ? "text" : "password"}
          autoComplete={field === "username" ? "username" : field === "password" || field === "current_password" ? "current-password" : "new-password"}
          {...register(field)} disabled={state.busy} aria-invalid={!!formState.errors[field]}
          aria-describedby={formState.errors[field] ? "account-validation" : undefined}
          className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent" />
      </div>)}
      {Object.keys(formState.errors).length > 0 && <p id="account-validation" role="alert" className="text-sm text-destructive">{t(password ? "account.passwordValidation" : "account.loginValidation")}</p>}
      {failed && <p role="alert" className="text-sm text-destructive">{t(password ? "account.passwordError" : "account.loginError")}</p>}
      <Button type="submit" className="w-full" disabled={state.busy || formState.isSubmitting}>{t(state.busy ? "account.working" : password ? "account.savePassword" : "account.signIn")}</Button>
      {password && <div className="flex justify-between gap-2">
        {!state.session?.user.must_change_password && <Button type="button" variant="ghost" size="sm" disabled={state.busy} onClick={() => controller.cancelPassword()}>{t("account.cancel")}</Button>}
        <Button type="button" variant="ghost" size="sm" disabled={state.busy} onClick={() => void controller.logout()}>{t("account.signOut")}</Button>
      </div>}
    </form>
  </section>;
}
